<template>
  <DefaultLayout background-image="green">
    <div
      class="raffle-page grid h-full w-full grid-rows-[auto,minmax(0,1fr),auto] justify-items-center overflow-hidden px-6 py-8 text-jm_white"
    >
      <div
        v-if="showLiveWatermark"
        class="raffle-live-card raffle-live-card--top absolute left-1/2 top-[calc(env(safe-area-inset-top,0px)+0.15rem)] z-20 w-[min(calc(100%-2rem),16.5rem)] -translate-x-1/2 overflow-hidden rounded-2xl border border-jm_white/25 bg-jm_dark_green/92 backdrop-blur-md"
      >
        <div class="raffle-live-marquee">
          <span>LIVE • JÄGERMEISTER • LIVE • JÄGERMEISTER • LIVE</span>
          <span>LIVE • JÄGERMEISTER • LIVE • JÄGERMEISTER • LIVE</span>
        </div>

        <div class="raffle-live-content px-3.5 py-2.5 text-center">
          <div class="flex items-center justify-center gap-2">
            <div class="raffle-live-pulse">
              <span></span>
              <span></span>
              <span></span>
            </div>
            <div class="text-[0.62rem] font-bold uppercase tracking-[0.22em] text-jm_white/85">
              {{ $t("raffle.live_badge") }}
            </div>
          </div>

          <div class="mt-1.5 text-[0.76rem] font-semibold leading-4 text-jm_white">
            {{ $t("raffle.live_instruction") }}
          </div>

          <div class="mt-2 rounded-xl bg-white px-3 py-2 text-jm_green">
            <div class="text-[0.56rem] font-bold uppercase tracking-[0.16em] text-jm_green/65">
              {{ $t("raffle.prize_label") }}
            </div>
            <div class="mt-0.5 text-[0.92rem] font-bold uppercase leading-4">
              {{ currentEntry.title }}
            </div>
            <div
              v-if="currentEntry.subtitle"
              class="mt-1 text-[0.68rem] font-semibold uppercase leading-3 text-jm_green/75"
            >
              {{ currentEntry.subtitle }}
            </div>
            <div
              v-if="campaignPrizeInformation"
              class="mt-1 text-[0.68rem] font-semibold uppercase leading-3 text-jm_green/75"
            >
              {{ campaignPrizeInformation }}
            </div>
          </div>

          <div class="mt-2.5 flex items-center justify-center gap-2.5">
            <div class="raffle-live-code rounded-xl bg-black/25 px-3 py-1.5">
              <div class="text-[0.58rem] uppercase tracking-[0.18em] text-jm_white/70">
                {{ $t("raffle.live_code_label") }}
              </div>
              <div class="mt-0.5 text-[1.05rem] font-bold tracking-[0.18em] text-jm_white">
                {{ liveVerificationCode }}
              </div>
            </div>

            <div class="text-[0.58rem] uppercase tracking-[0.1em] text-jm_white/80">
              {{ liveTimestamp }}
            </div>
          </div>
        </div>
      </div>

      <div class="flex flex-col items-center">
        <img :src="logo" alt="logo" class="mb-6 h-20" />
        <div class="text-center text-[2rem] font-bold uppercase leading-none">
          {{ $t("raffle.title") }}
        </div>
        <div class="mt-3 max-w-[18rem] text-center text-[1rem] leading-6">
          {{ $t("raffle.description") }}
        </div>
      </div>

      <div class="raffle-stage flex min-h-0 w-full max-w-[22rem] flex-col items-center justify-center">
        <div class="raffle-wheel relative flex items-center justify-center">
          <div
            class="absolute inset-0 animate-spin"
            :class="{ 'animate-none': !isSpinning }"
          >
            <img
              :src="require('@/assets/images/misc/circular.svg')"
              class="h-full w-full object-cover scale-110"
            />
          </div>

          <div
            class="absolute inset-6 flex flex-col items-center justify-center rounded-full border-2 border-jm_white/30 bg-white text-center text-jm_green shadow-2xl"
          >
            <div
              class="px-6 text-[1.4rem] font-bold uppercase leading-tight"
              :class="currentEntry.type === 'prize' ? 'text-jm_orange' : 'text-jm_green'"
            >
              {{ currentEntry.title }}
            </div>
            <div
              class="mt-3 max-w-[12rem] text-sm uppercase leading-5 text-jm_green/80"
            >
              {{ currentEntry.subtitle }}
            </div>
          </div>
        </div>

        <div
          v-if="raffleError"
          class="mt-4 max-w-[18rem] text-center text-sm leading-5 text-jm_orange"
        >
          {{ raffleError }}
        </div>
      </div>

      <div class="raffle-actions relative flex w-full max-w-[22rem] flex-col gap-3 pb-4">
        <button
          v-if="!hasStopped"
          class="min-h-12 rounded-xl bg-jm_orange px-4 text-sm font-bold uppercase tracking-[0.15em] text-white"
          :disabled="isResolvingResult"
          :class="{ 'cursor-not-allowed opacity-50': isResolvingResult }"
          @click="toggleRaffle"
        >
          {{
            isSpinning
              ? $t("raffle.stop_button")
              : isResolvingResult
                ? $t("raffle.loading_button")
                : $t("raffle.start_button")
          }}
        </button>

        <button
          v-else
          class="min-h-12 rounded-xl bg-jm_orange px-4 text-sm font-bold uppercase tracking-[0.15em] text-white"
          @click="finishRaffle"
        >
          {{ $t("raffle.finish_button") }}
        </button>
      </div>
    </div>
  </DefaultLayout>
</template>

<script lang="ts" setup>
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import DefaultLayout from "../layouts/DefaultLayout.vue";
import logo from "@/assets/images/misc/logo_jaegermeister_white.svg";
import { useGlobalStore } from "@/stores/global";
import { useGameTracking } from "@/composables/useGameTracking";
import { claimRaffleResult } from "@/services/raffle";
import i18n from "@/plugins/i18n";
import {
  getPimRaffleConfig,
  type PimRaffleEntry,
} from "@/services/pimRaffleConfig";

const pimUnavailableFallbackEntries: PimRaffleEntry[] = [
  {
    type: "lose",
    title: "Nincs nyeremény",
    subtitle: "Legközelebb több szerencsét!",
  },
];

const globalStore = useGlobalStore();
const { kpiTracker, session } = useGameTracking();

const currentIndex = ref(0);
const isSpinning = ref(false);
const hasStopped = ref(false);
const isResolvingResult = ref(false);
const raffleError = ref("");
const raffleEntries = ref<PimRaffleEntry[]>([]);
const prizeProbability = ref(0.1);
const targetEntry = ref<PimRaffleEntry | null>(null);
const liveVerificationCode = ref("---");
const liveTimestamp = ref("--:--:--");
const campaignPrizeInformation = ref("");
let spinInterval: ReturnType<typeof setInterval> | null = null;
let liveWatermarkInterval: ReturnType<typeof setInterval> | null = null;

const visibleEntries = computed(() => raffleEntries.value);

const idleEntry = computed<PimRaffleEntry>(() => ({
  title: i18n.global.t("raffle.idle_title"),
  subtitle: i18n.global.t("raffle.idle_subtitle"),
  type: "lose",
}));

const currentEntry = computed(
  () =>
    (!isSpinning.value && !hasStopped.value
      ? idleEntry.value
      : visibleEntries.value[currentIndex.value]) || idleEntry.value
);
const showLiveWatermark = computed(
  () => hasStopped.value && globalStore.raffleResult === "win"
);

const clearSpinInterval = () => {
  if (spinInterval) {
    clearInterval(spinInterval);
    spinInterval = null;
  }
};

const clearLiveWatermarkInterval = () => {
  if (liveWatermarkInterval) {
    clearInterval(liveWatermarkInterval);
    liveWatermarkInterval = null;
  }
};

const buildLiveVerificationCode = () => {
  const sessionIdentifier = session.fingerprint.value || "guest";
  const timeBucket = Math.floor(Date.now() / 4000);
  const source = `${sessionIdentifier}-${timeBucket}`;
  let hash = 0;

  for (let index = 0; index < source.length; index += 1) {
    hash = (hash * 31 + source.charCodeAt(index)) | 0;
  }

  return String((Math.abs(hash) % 9000) + 1000);
};

const updateLiveWatermark = () => {
  liveVerificationCode.value = buildLiveVerificationCode();
  liveTimestamp.value = new Date().toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

const resolveRaffleResult = async () => {
  const gameId = process.env.VUE_APP_GAME_ID || "";
  const identifier = session.fingerprint.value || "";

  if (!gameId || !identifier) {
    throw new Error("Missing game or session information for the raffle.");
  }

  const response = await claimRaffleResult(gameId, identifier);
  globalStore.setRaffleResult(response.result);
  campaignPrizeInformation.value =
    response.result === "win" ? response.campaign_information?.trim() || "" : "";
  return response.result;
};

const ensureRaffleConfig = async () => {
  try {
    const config = await getPimRaffleConfig();
    raffleEntries.value = config.prizes;
    prizeProbability.value = config.probability;
  } catch (error) {
    console.error("[RaffleMove] Error while loading PIM raffle config:", error);
    raffleEntries.value = pimUnavailableFallbackEntries;
    prizeProbability.value = 0;
  }
};

const randomEntry = (entries: PimRaffleEntry[]) =>
  entries[Math.floor(Math.random() * entries.length)];

const resolveTargetEntry = async () => {
  await ensureRaffleConfig();

  const prizeEntries = raffleEntries.value.filter(
    (entry) => entry.type === "prize"
  );
  const loseEntry =
    raffleEntries.value.find((entry) => entry.type === "lose");

  if (!loseEntry) {
    throw new Error("Missing lose entry in raffle configuration.");
  }

  const isWinCandidate =
    prizeEntries.length > 0 && Math.random() < prizeProbability.value;

  if (!isWinCandidate) {
    globalStore.setRaffleResult("lose");
    return loseEntry;
  }

  const claimResult = await resolveRaffleResult();

  if (claimResult === "win" && prizeEntries.length > 0) {
    return randomEntry(prizeEntries);
  }

  globalStore.setRaffleResult("lose");
  return loseEntry;
};

const startRaffle = async () => {
  raffleError.value = "";
  isResolvingResult.value = true;

  try {
    targetEntry.value = await resolveTargetEntry();
    currentIndex.value = Math.floor(Math.random() * visibleEntries.value.length);
    isSpinning.value = true;
    spinInterval = setInterval(() => {
      currentIndex.value = Math.floor(Math.random() * visibleEntries.value.length);
    }, 130);
  } catch (error) {
    console.error("[RaffleMove] Error while claiming raffle result:", error);
    raffleError.value =
      error instanceof Error
        ? error.message
        : "The raffle result could not be loaded.";
  } finally {
    isResolvingResult.value = false;
  }
};

const stopRaffle = () => {
  clearSpinInterval();
  if (targetEntry.value) {
    const targetIndex = visibleEntries.value.findIndex(
      (entry) =>
        entry.type === targetEntry.value?.type &&
        entry.title === targetEntry.value?.title &&
        entry.subtitle === targetEntry.value?.subtitle
    );

    if (targetIndex >= 0) {
      currentIndex.value = targetIndex;
    }
  }
  isSpinning.value = false;
  hasStopped.value = true;
};

const toggleRaffle = async () => {
  if (isResolvingResult.value) {
    return;
  }

  globalStore.playTapSound();

  if (isSpinning.value) {
    stopRaffle();
    return;
  }

  await startRaffle();
};

const finishRaffle = () => {
  globalStore.playTapSound();
  globalStore.changeStep(
    globalStore.shouldShowDailyLimitScreen ? "thatsEnough" : "ageCheck"
  );
  globalStore.setShouldShowDailyLimitScreen(false);
  globalStore.setCapturedPhotoFile(null);
  globalStore.setCapturedPhotoDataUrl(null);
  globalStore.setUploadedPhotoItemId(null);
  globalStore.setRaffleResult(null);
  campaignPrizeInformation.value = "";
  targetEntry.value = null;
  kpiTracker.resetGame();
  kpiTracker.resetTrackingData();
  globalStore.resetGame(kpiTracker.selectedPerson.value);
};

onMounted(() => {
  updateLiveWatermark();
  liveWatermarkInterval = setInterval(updateLiveWatermark, 1000);
});

onBeforeUnmount(() => {
  clearSpinInterval();
  clearLiveWatermarkInterval();
});
</script>

<style lang="scss" scoped>
@import "@/assets/sass/main.sass";

.raffle-page {
  padding-bottom: calc(env(safe-area-inset-bottom, 0px) + 1rem);
  row-gap: 0.75rem;
}

.raffle-stage {
  min-height: 0;
}

.raffle-wheel {
  height: min(100%, 18rem);
  width: min(100%, 18rem);
  aspect-ratio: 1 / 1;
  flex: 0 1 auto;
}

.raffle-actions {
  align-self: stretch;
}

.raffle-live-card {
  box-shadow: 0 18px 45px rgba(0, 0, 0, 0.22);
}

.raffle-live-card--top {
  background: linear-gradient(
    180deg,
    rgba(15, 45, 27, 0.96),
    rgba(24, 72, 52, 0.9)
  );
}

.raffle-live-marquee {
  display: flex;
  gap: 1rem;
  overflow: hidden;
  white-space: nowrap;
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
  background: linear-gradient(90deg, rgba(237, 116, 43, 0.28), rgba(255, 255, 255, 0.03));
  font-size: 0.54rem;
  font-weight: 700;
  letter-spacing: 0.22em;
  padding: 0.34rem 0.6rem;
  text-transform: uppercase;
}

.raffle-live-marquee span {
  animation: live-marquee 9s linear infinite;
}

.raffle-live-code {
  min-width: 6.7rem;
}

.raffle-live-pulse {
  position: relative;
  width: 1.35rem;
  height: 1.35rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.raffle-live-pulse span {
  position: absolute;
  border-radius: 9999px;
  border: 1px solid rgba(255, 255, 255, 0.45);
  animation: live-pulse 2.2s ease-out infinite;
}

.raffle-live-pulse span:nth-child(1) {
  width: 0.35rem;
  height: 0.35rem;
  background: rgba(237, 116, 43, 0.9);
  animation: none;
}

.raffle-live-pulse span:nth-child(2) {
  width: 0.85rem;
  height: 0.85rem;
}

.raffle-live-pulse span:nth-child(3) {
  width: 1.35rem;
  height: 1.35rem;
  animation-delay: 0.65s;
}

.animate-spin {
  animation: rotate 15s linear infinite;
}

.animate-none {
  animation: none;
}

@keyframes rotate {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

@keyframes live-marquee {
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-100%);
  }
}

@keyframes live-pulse {
  0% {
    opacity: 0.85;
    transform: scale(0.65);
  }
  100% {
    opacity: 0;
    transform: scale(1.15);
  }
}

@media (max-height: 760px) {
  .raffle-page {
    padding-top: 1rem;
    padding-bottom: calc(env(safe-area-inset-bottom, 0px) + 0.75rem);
  }

  .raffle-wheel {
    height: min(100%, 16rem);
    width: min(100%, 16rem);
  }
}

@media (max-height: 680px) {
  .raffle-page {
    padding-top: 0.75rem;
    padding-left: 1rem;
    padding-right: 1rem;
    row-gap: 0.5rem;
  }

  .raffle-wheel {
    height: min(100%, 14rem);
    width: min(100%, 14rem);
  }

  .raffle-live-card--top {
    width: min(calc(100% - 1.5rem), 14.75rem);
  }
}
</style>
